<template>
  <div class="news-detail-page">
    <template v-if="news">
      <div class="content-wrap">
        <button class="back-btn" @click="goBack">← 返回新闻列表</button>
        <span class="category-tag">{{ categoryLabel }}</span>
        <h1 class="news-title">{{ news.title }}</h1>
        <p class="news-date">{{ news.date }}</p>
        <div class="news-body" v-html="formattedContent" />
      </div>
    </template>

    <template v-else>
      <div class="fallback-wrap">
        <h2 class="fallback-title">文章未找到</h2>
        <p class="fallback-desc">该文章不存在或已被移除</p>
        <button class="fallback-btn" @click="goBack">← 返回新闻列表</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { newsItems, type NewsItem } from '../data/portalData'
import { getPortalArticle } from '@/api/portal'

defineOptions({ name: 'NewsDetailPage' })

const route = useRoute()
const router = useRouter()

const news = ref<NewsItem | undefined>()

const categoryMap: Record<string, string> = {
  product: '产品更新',
  company: '公司动态',
  industry: '行业资讯'
}

const categoryLabel = computed(() => {
  if (!news.value) return ''
  return categoryMap[news.value.category] || news.value.category
})

const formattedContent = computed(() => {
  if (!news.value) return ''
  return news.value.content.replace(/\n/g, '<br>')
})

onMounted(async () => {
  const id = route.params.id as string
  try {
    const article = await getPortalArticle(id)
    if (article) {
      let extra: Record<string, string> = {}
      try { extra = JSON.parse(article.extra || '{}') } catch {}
      const date = article.publishTime ? new Date(article.publishTime) : new Date()
      news.value = {
        id: String(article.id),
        title: article.title,
        summary: article.summary || '',
        category: extra.category || 'company',
        date: date.toLocaleDateString('zh-CN'),
        day: String(date.getDate()).padStart(2, '0'),
        month: `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`,
        content: article.content || ''
      }
    }
  } catch {}
  if (!news.value) {
    news.value = newsItems.find((n) => n.id === id)
  }
})

function goBack() {
  router.push('/portal/news')
}
</script>

<style lang="scss" scoped>
.content-wrap {
  padding: 64px 24px;
  max-width: 800px;
  margin: 0 auto;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #0066ff;
  font-size: 14px;
  margin-bottom: 24px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
}

.category-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: #F3F4F6;
  color: #4B5563;
  margin-bottom: 16px;
}

.news-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
  color: #111827;
  line-height: 1.3;
}

.news-date {
  font-size: 14px;
  color: #9CA3AF;
  margin-bottom: 24px;
}

.news-body {
  font-size: 16px;
  line-height: 2;
  color: #4B5563;
}

.fallback-wrap {
  text-align: center;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.fallback-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
}

.fallback-desc {
  color: #6B7280;
  margin-bottom: 24px;
}

.fallback-btn {
  background: none;
  border: 1px solid #0066ff;
  color: #0066ff;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: #0066ff;
    color: #fff;
  }
}
</style>
